import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Briefcase, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services" },
  { label: "Industries", to: "/industries" },
  { label: "Jobs", to: "/jobs" },
  { label: "For Employers", to: "/for-employers" },
  { label: "For Job Seekers", to: "/for-job-seekers" },
  { label: "Locations", to: "/locations" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { settings } = useSite();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();

  const dashPath = user && (user.role === "admin" || user.role === "staff")
    ? "/admin" : user && user.role === "employer" ? "/employer/dashboard" : "/candidate/dashboard";
  const handleLogout = async () => { await logout(); setOpen(false); navigate("/"); };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-strong shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img src={settings.logo_url || "/logo.png"} alt="Dreamzz HR & Consultancy" className="h-11 w-auto" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:text-brand-emerald ${
                loc.pathname === n.to ? "text-brand-emerald" : "text-slate-300"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          {user && (user.role === "admin" || user.role === "staff") ? (
            <Link to="/admin" data-testid="nav-admin-link">
              <Button variant="outline" className="border-brand-emerald/40 text-brand-emerald hover:bg-brand-emerald/10">Admin</Button>
            </Link>
          ) : user && (user.role === "candidate" || user.role === "employer") ? (
            <Link to={user.role === "employer" ? "/employer/dashboard" : "/candidate/dashboard"} data-testid="nav-dashboard-link">
              <Button variant="outline" className="border-brand-emerald/40 text-brand-emerald hover:bg-brand-emerald/10">Dashboard</Button>
            </Link>
          ) : (
            <Link to="/login" data-testid="nav-login-link">
              <Button variant="ghost" className="text-slate-300">Login</Button>
            </Link>
          )}
          <Link to="/for-employers" data-testid="nav-hire-cta">
            <Button className="btn-press bg-brand-emerald hover:bg-[#C62828] text-white font-semibold glow-primary">
              Hire Talent
            </Button>
          </Link>
          {user && (
            <Button variant="ghost" size="icon" data-testid="nav-logout" onClick={handleLogout}
              title="Logout" aria-label="Logout" className="text-slate-400 hover:text-brand-emerald">
              <LogOut size={18} />
            </Button>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden p-2 text-slate-200"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden glass-strong border-t border-white/10 animate-fadeUp">
          <div className="px-4 py-4 grid gap-1 max-h-[75vh] overflow-y-auto">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-3 py-3 rounded-lg text-slate-200 hover:bg-white/5 font-medium"
              >
                {n.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              {user ? (
                <Link to={dashPath} className="flex-1"><Button variant="outline" className="w-full">{user.role === "admin" || user.role === "staff" ? "Admin" : "Dashboard"}</Button></Link>
              ) : (
                <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">Login</Button></Link>
              )}
              <Link to="/for-employers" className="flex-1"><Button className="w-full bg-brand-emerald text-white">Hire Talent</Button></Link>
            </div>
            {user && (
              <Button variant="outline" data-testid="mobile-logout" onClick={handleLogout}
                className="w-full mt-2 border-white/15 text-slate-600">
                <LogOut size={16} className="mr-2" />Logout
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
